/**
 * Save a research document to Files.
 *
 * No new endpoint: the .docx is the one `artifactExport` already builds for
 * "Download as Word", and it goes through the same `importFilesSSE` upload
 * the Files page uses. It lands in a top-level "Research" folder - the
 * existing one when there is one, otherwise `FolderName` creates it (the
 * pattern the contract library upload uses).
 */

import { fetchFolderTree, FolderTreeItem, importFilesSSE } from '../../store/actions/FolderAction';
import { artifactFileName, artifactToDocxBlob } from './artifactExport';
import { ResearchResponse } from './types';

export const RESEARCH_FOLDER = 'Research';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** The top-level Research folder's id, when it already exists. */
export function researchFolderId(tree: FolderTreeItem[] | null | undefined): string | undefined {
  const match = (tree || []).find(
    (f) => !f?.parent_id
      && String(f?.label || '').trim().toLowerCase() === RESEARCH_FOLDER.toLowerCase()
  );
  return match?.id ? String(match.id) : undefined;
}

/**
 * Upload the document as a .docx. Resolves with the uploaded file name;
 * rejects when nothing was stored, so the caller can say so.
 */
export const saveArtifactToFiles =
  (response: ResearchResponse, title: string) =>
  async (dispatch: any): Promise<string> => {
    const fileName = `${artifactFileName(title)}.docx`;
    const blob = await artifactToDocxBlob(response, title);
    const file = new File([blob], fileName, { type: DOCX_MIME });

    let folderId: string | undefined;
    try {
      folderId = researchFolderId(await dispatch(fetchFolderTree()));
    } catch {
      folderId = undefined;
    }
    const payload: Record<string, any> = folderId
      ? { FolderID: folderId }
      : { FolderName: RESEARCH_FOLDER };

    const res: any = await dispatch(importFilesSSE(payload, [file]));
    const uploaded: any[] = res?.filesUploaded || [];
    if (!res?.streamEnded && !uploaded.length && !res?.ok) {
      throw new Error('upload-failed');
    }
    return fileName;
  };
